import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Camera, CameraOff, Check, Keyboard, CircleCheck, Circle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useScanner } from "@/components/useScanner";

export const InventoryPanel = ({ data, onDone, onCancel }) => {
  const [scanned, setScanned] = useState([]);
  const [excluded, setExcluded] = useState([]);
  const [manual, setManual] = useState("");
  const [busy, setBusy] = useState(false);

  const bottles = useMemo(() => data.wines.flatMap((w) => w.bottles.map((b) => ({ ...b, wine: w }))), [data]);
  const boxBottles = useMemo(() => Object.fromEntries(data.boxes.map((x) => [x.barcode, bottles.filter((b) => b.box_number === x.box_number).map((b) => b.barcode)])), [data, bottles]);
  const found = new Set(scanned.flatMap((c) => boxBottles[c] || [c]).filter((c) => !excluded.includes(c)));
  const known = new Set([...bottles.map((b) => b.barcode), ...Object.keys(boxBottles)]);
  const extra = scanned.filter((c) => !known.has(c));
  const count = bottles.filter((b) => found.has(b.barcode)).length;

  const add = (raw) => {
    const code = raw.trim().toUpperCase();
    if (!code) return;
    setScanned((s) => (s.includes(code) ? s : [...s, code]));
    setExcluded((x) => x.filter((c) => c !== code && !(boxBottles[code] || []).includes(c)));
    if (navigator.vibrate) navigator.vibrate(60);
  };
  const toggle = (code) => {
    if (boxBottles[code]) {
      setExcluded((x) => x.filter((c) => !boxBottles[code].includes(c)));
      return setScanned((s) => (s.includes(code) ? s.filter((c) => c !== code) : [...s, code]));
    }
    if (found.has(code)) {
      setScanned((s) => s.filter((c) => c !== code));
      setExcluded((x) => [...x, code]);
    } else {
      setExcluded((x) => x.filter((c) => c !== code));
      setScanned((s) => [...s, code]);
    }
  };
  const scanner = useScanner(add, true);

  const confirm = async () => {
    setBusy(true);
    try {
      await scanner.stop();
      const { data: res } = await api.post(`/locations/${data.location.id}/inventory`, { barcodes: [...bottles.filter((b) => found.has(b.barcode)).map((b) => b.barcode), ...extra] });
      toast.success("Inventory recorded");
      onDone(res);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-[#D4AF37]/40 bg-[#161214] p-5 mb-8 rise" data-testid="inventory-panel">
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="font-serif text-2xl text-[#F5F0EB]">Inventory check</h3>
        <p className="font-mono text-sm text-[#D4AF37]" data-testid="inventory-progress">{count} / {bottles.length}</p>
      </div>
      <p className="text-sm text-[#A89F91] mb-4">Scan every bottle (or box) you find here, or tap them in the list. Then confirm to record the date and see what is missing.</p>
      <div className="relative aspect-square max-h-[70vh] w-full max-w-lg mx-auto rounded-lg overflow-hidden border border-[#2E252A] bg-[#0D0A0B] mb-3">
        <div id="qr-reader" className="absolute inset-0" data-testid="inventory-camera-view" />
        {scanner.on && <div className="laser absolute left-[8%] right-[8%] h-0.5 bg-[#8C2B36] shadow-[0_0_12px_#8C2B36] pointer-events-none" />}
      </div>
      <div className="flex gap-2 mb-5">
        <Button type="button" onClick={scanner.on ? scanner.stop : scanner.start} className="h-12 bg-[#5C1D24] hover:bg-[#8C2B36] text-[#F5F0EB]" data-testid="inventory-camera-button">
          {scanner.on ? <CameraOff className="w-4 h-4" /> : <Camera className="w-4 h-4" />}
        </Button>
        <form className="flex flex-1 gap-2" onSubmit={(e) => { e.preventDefault(); add(manual); setManual(""); }}>
          <div className="relative flex-1">
            <Keyboard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6E665D]" />
            <Input value={manual} onChange={(e) => setManual(e.target.value)} placeholder="Type a code" className="pl-10 h-12 font-mono bg-[#0D0A0B]" data-testid="inventory-manual-input" />
          </div>
          <Button type="submit" variant="outline" className="h-12" data-testid="inventory-manual-add">Add</Button>
        </form>
      </div>

      <div className="space-y-1 max-h-80 overflow-y-auto mb-4" data-testid="inventory-checklist">
        {data.boxes.map((x) => (
          <CheckRow key={x.barcode} on={scanned.includes(x.barcode)} onClick={() => toggle(x.barcode)} code={x.barcode} label={`Box ${x.box_number} · ${x.bottle_count} bottles`} />
        ))}
        {bottles.map((b) => (
          <CheckRow key={b.barcode} on={found.has(b.barcode)} onClick={() => toggle(b.barcode)} code={b.barcode} label={`${b.wine.name} ${b.wine.vintage || "NV"}${b.box_number ? ` · box ${b.box_number}` : ""}`} />
        ))}
      </div>
      {!!extra.length && (
        <p className="text-xs text-[#E5A93C] mb-4" data-testid="inventory-extra">Not expected here: {extra.join(", ")}</p>
      )}
      <div className="flex gap-2 justify-end">
        <Button variant="ghost" onClick={async () => { await scanner.stop(); onCancel(); }} data-testid="inventory-cancel-button">Cancel</Button>
        <Button onClick={confirm} disabled={busy} className="bg-[#D4AF37] hover:bg-[#E5A93C] text-[#0D0A0B]" data-testid="inventory-confirm-button">
          <Check className="w-4 h-4 mr-2" />{busy ? "Saving…" : "Confirm inventory"}
        </Button>
      </div>
    </div>
  );
};

const CheckRow = ({ on, onClick, code, label }) => (
  <button type="button" onClick={onClick} className={`w-full flex items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors ${on ? "bg-[#2E5D4B]/20" : "hover:bg-[#1E181B]"}`} data-testid={`inventory-item-${code}`}>
    {on ? <CircleCheck className="w-4 h-4 text-emerald-400 shrink-0" /> : <Circle className="w-4 h-4 text-[#4A3B43] shrink-0" />}
    <span className="font-mono text-xs text-[#F5F0EB] w-28 shrink-0">{code}</span>
    <span className="text-[#A89F91] truncate">{label}</span>
  </button>
);
